import axios from 'axios';
import {
  API_BASE_URL,
  ENABLE_API_LOGS,
  UI_LOG_LEVEL,
} from '../config/env';
import { getSecurityHeaders } from '../config/securityHeaders';

export { API_BASE_URL };
export { RAG_API_URL } from '../config/env';
export { UI_LOG_LEVEL, ENABLE_API_LOGS };
export { getSecurityHeaders } from '../config/securityHeaders';

const ACCESS_TOKEN_KEYS = [
  'comply_access_token',
  'access_token',
  'auth_token',
  'token',
];

const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const shouldLog = (level) => (
  ENABLE_API_LOGS &&
  UI_LOG_LEVEL !== 'silent' &&
  LOG_LEVELS[level] <= (LOG_LEVELS[UI_LOG_LEVEL] ?? LOG_LEVELS.warn)
);

const logApiEvent = (level, message, meta = {}) => {
  if (!shouldLog(level)) return;
  const method = meta.method?.toUpperCase();
  const details = {
    ...(method ? { method } : {}),
    ...(meta.url ? { url: meta.url } : {}),
    ...(meta.status ? { status: meta.status } : {}),
    ...(meta.durationMs !== undefined ? { durationMs: meta.durationMs } : {}),
  };
  console[level === 'debug' ? 'debug' : level](`[api] ${message}`, details);
};

export class ApiClientError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'ApiClientError';
    this.status = options.status || null;
    this.code = options.code || null;
    this.details = options.details || null;
    this.payload = options.payload || null;
    this.isApiClientError = true;
  }
}

export const getStoredAccessToken = () => {
  for (const key of ACCESS_TOKEN_KEYS) {
    const token = localStorage.getItem(key);
    if (token) return token;
  }
  return '';
};

export const clearStoredAccessTokens = () => {
  ACCESS_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
};

export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
    this.code = 'SESSION_EXPIRED';
  }
}

export const isSessionExpiredError = (error) => (
  error?.name === 'SessionExpiredError' ||
  error?.code === 'SESSION_EXPIRED'
);

/** Clear stored session and send the user to login (full page navigation). */
export const handleUnauthorizedSession = () => {
  clearStoredAccessTokens();
  localStorage.removeItem('userInfo');
  localStorage.removeItem('comply_user');
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

export const setStoredAccessToken = (token) => {
  if (!token) return;
  localStorage.setItem('comply_access_token', token);
  localStorage.setItem('access_token', token);
  localStorage.setItem('auth_token', token);
  localStorage.setItem('token', token);
};

export const normalizeApiError = (error) => {
  if (error?.isApiClientError) return error;

  const status = error?.response?.status || null;
  const payload = error?.response?.data || null;
  const message =
    payload?.message ||
    payload?.error?.message ||
    (typeof payload?.error === 'string' ? payload.error : null) ||
    payload?.error ||
    payload?.detail ||
    error?.message ||
    'Request failed';

  return new ApiClientError(message, {
    status,
    code: payload?.code || payload?.error?.code || error?.code || null,
    details: payload?.details || payload?.error?.details || payload?.detail || null,
    payload,
  });
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  config.metadata = {
    ...(config.metadata || {}),
    startedAt: Date.now(),
  };
  config.headers = {
    ...getSecurityHeaders(),
    ...(config.headers || {}),
  };
  const token = getStoredAccessToken();
  if (token && !config.headers?.Authorization) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  logApiEvent('debug', 'request started', {
    method: config.method,
    url: config.url,
  });
  return config;
});

api.interceptors.response.use(
  (response) => {
    logApiEvent('debug', 'request completed', {
      method: response.config?.method,
      url: response.config?.url,
      status: response.status,
      durationMs: Date.now() - (response.config?.metadata?.startedAt || Date.now()),
    });
    return response;
  },
  (error) => {
    const normalized = normalizeApiError(error);
    logApiEvent(normalized.status >= 500 ? 'error' : 'warn', 'request failed', {
      method: error?.config?.method,
      url: error?.config?.url,
      status: normalized.status,
      durationMs: Date.now() - (error?.config?.metadata?.startedAt || Date.now()),
    });
    if (normalized.status === 401) {
      handleUnauthorizedSession();
    }
    return Promise.reject(normalized);
  },
);

export const getAuthParams = () => ({});

export default api;
